import { useState, useCallback, useEffect } from 'react';

interface Threshold {
  id: string;
  color: string;
  rating: string;
  logic: string;
  value: string | [number, number];
}

export const useOverlapDetection = (
  scoreConfig: Threshold[],
  isScoring: boolean,
) => {
  const [overlappingIds, setOverlappingIds] = useState<Set<string>>(new Set());

  // Helper function to convert threshold to range [min, max]
  const getThresholdRange = useCallback(
    (threshold: Threshold): [number, number] | null => {
      const { logic, value } = threshold;

      if (logic === 'btw' && Array.isArray(value)) {
        // Skip ranges with NaN values to avoid overlap detection issues
        if (isNaN(value[0]) || isNaN(value[1])) return null;
        return [value[0], value[1]];
      }

      if (typeof value === 'string') {
        const numValue = Number(value);
        if (isNaN(numValue)) return null;

        switch (logic) {
          case 'eq':
            return [numValue, numValue];
          case 'gt':
            return [numValue + 0.01, 100];
          case 'gte':
            return [numValue, 100];
          case 'lt':
            return [0, numValue - 0.01];
          case 'lte':
            return [0, numValue];
          default:
            return null;
        }
      }

      return null;
    },
    [],
  );

  // Helper function to check if two ranges overlap
  const rangesOverlap = useCallback(
    (range1: [number, number], range2: [number, number]): boolean => {
      const [a1, b1] = range1;
      const [a2, b2] = range2;
      return a1 <= b2 && a2 <= b1;
    },
    [],
  );

  // Function to detect overlapping thresholds
  const detectOverlaps = useCallback((): Set<string> => {
    const overlaps = new Set<string>();

    for (let i = 0; i < scoreConfig.length; i++) {
      for (let j = i + 1; j < scoreConfig.length; j++) {
        const range1 = getThresholdRange(scoreConfig[i]);
        const range2 = getThresholdRange(scoreConfig[j]);

        if (range1 && range2 && rangesOverlap(range1, range2)) {
          overlaps.add(scoreConfig[i].id);
          overlaps.add(scoreConfig[j].id);
        }
      }
    }

    return overlaps;
  }, [scoreConfig, getThresholdRange, rangesOverlap]);

  // Check for overlaps whenever scoreConfig changes
  useEffect(() => {
    if (isScoring && scoreConfig.length > 1) {
      const overlaps = detectOverlaps();
      setOverlappingIds(overlaps);
    } else {
      setOverlappingIds(new Set());
    }
  }, [scoreConfig, isScoring, detectOverlaps]);

  return {
    overlappingIds,
    detectOverlaps,
    hasOverlaps: overlappingIds.size > 0,
  };
};
